import { useQueries } from '@tanstack/react-query';
import { useLocation } from '../context/LocationContext';
import { useEffect, useRef, useState } from 'react';
import { usePaymentMethodsStore } from '../context/PaymentMethodStore';
import {
  getGetTerminalStatusQueryOptions,
  type TerminalStatusResponseLatestState as TerminalState,
} from '@ordr-as/payment-service-types/epos';

const POLLING_START_DELAY_MS = 2000;

export const useBusyTerminalPolling = (busyTerminalIds: string[]) => {
  const { location } = useLocation();
  const setTerminalLatestState = usePaymentMethodsStore((s) => s.actions.setTerminalLatestState);
  const prevLatestStatesRef = useRef<Record<string, TerminalState>>({});
  const [isReady, setIsReady] = useState(false);

  // Delay polling start by 2 seconds when terminals become busy
  // Depends on array content so delay resets when different terminals become busy
  useEffect(() => {
    if (busyTerminalIds.length === 0) {
      setIsReady(false);
      return;
    }

    setIsReady(false);
    const timer = setTimeout(() => {
      setIsReady(true);
    }, POLLING_START_DELAY_MS);

    return () => clearTimeout(timer);
  }, [busyTerminalIds]);

  // Clean up states for terminals no longer being tracked
  useEffect(() => {
    const busySet = new Set(busyTerminalIds);
    for (const terminalId of Object.keys(prevLatestStatesRef.current)) {
      if (!busySet.has(terminalId)) {
        delete prevLatestStatesRef.current[terminalId];
      }
    }
  }, [busyTerminalIds]);

  const results = useQueries({
    queries: busyTerminalIds.map((terminalId) =>
      getGetTerminalStatusQueryOptions(location.id, terminalId, {
        query: {
          refetchInterval: 3000,
          enabled: isReady && busyTerminalIds.length > 0,
        },
      })
    ),
  });

  // Update store with latest states from fast polling
  useEffect(() => {
    results.forEach((result, index) => {
      const terminalId = busyTerminalIds[index];
      // Verify IDs match in case of timing mismatch between queries and busyTerminalIds
      if (result.data && terminalId && result.data.pspTerminalId === terminalId) {
        const latestState = result.data.latestState;

        // Only update if state changed
        if (prevLatestStatesRef.current[terminalId] !== latestState) {
          prevLatestStatesRef.current[terminalId] = latestState;
          setTerminalLatestState(terminalId, latestState);
        }
      }
    });
  }, [results, busyTerminalIds, setTerminalLatestState]);
};
